<template>
    <div class="availability-schedule availability-schedule--with-scroll-container">
        <!-- Error banner hidden for Figma UI match -->
        <!-- <div v-if="loadError" class="availability-schedule__banner availability-schedule__banner--error" role="alert">
            <span>{{ loadError }}</span>
            <button type="button" class="availability-schedule__retry" @click="getCalendarDays()">
                {{ $t('elements.availability.retry') }}
            </button>
        </div> -->
        <div class="availability-schedule__scroll-container">
            <availability-full-calendar
                :year="year"
                :month="month"
                :dates="dates"
                :physician="user"
                :practice="practice"
                :location="location"
                :showCancelled="showCancelled"
                :loading="loading"
                @schedule-changed="getCalendarDays(true)"
            ></availability-full-calendar>
        </div>
    </div>
</template>

<script>
    import dayjs from 'dayjs'
    import UsersAPI from 'api/UsersAPI'
    import Store from '~/store'
    import ReferralStatusEnum from '../../enums/ReferralStatusEnum'
    import AvailabilityFullCalendar from './availability/AvailabilityFullCalendar'

    export default {
        components: {
            AvailabilityFullCalendar,
        },

        props: {
            practice: { type: Object, required: true },
            user: { type: Object, required: true },
            practiceLocationId: { type: Number, default: undefined },
            currentDate: { type: Date, default: () => new Date() },
        },

        data() {
            return {
                location: null,
                year: dayjs().year(),
                month: dayjs().month(),
                dates: {},
                showCancelled: false,
                loading: false,
                loadError: '',
                loadedRange: null,
                loadedLocationId: undefined,
            }
        },

        computed: {
            currentView() {
                return this.$route.query.view || 'monthly'
            },
        },

        methods: {
            // Range of days the current view needs to display.
            visibleRange() {
                const base = dayjs().set('month', this.month).set('year', this.year)

                if (this.currentView === 'daily') {
                    const anchor = this.$route.query.date ? dayjs(this.$route.query.date) : dayjs()
                    return { from: anchor.startOf('day'), to: anchor.startOf('day') }
                }
                if (this.currentView === 'weekly') {
                    const anchor = this.$route.query.date ? dayjs(this.$route.query.date) : base
                    return { from: anchor.startOf('week'), to: anchor.endOf('week') }
                }
                return { from: base.startOf('month'), to: base.endOf('month') }
            },

            getCalendarDays(force = false) {
                const visible = this.visibleRange()

                // Reuse the cached month data when the visible range is already loaded
                // for the same location (e.g. switching to weekly/daily within the same
                // month, or navigating to another day in the loaded week).
                if (!force && this.isRangeLoaded(visible)) {
                    return
                }

                // Always load the full month covering the visible range so subsequent
                // day/week switches within it don't trigger another request.
                const dateFrom = visible.from.startOf('month').startOf('day').format('YYYY-MM-DD')
                const dateTo = visible.to.endOf('month').startOf('day').format('YYYY-MM-DD')

                this.getSchedule(this.user.id, this.practice.id, dateFrom, dateTo, this.practiceLocationId)
            },

            isRangeLoaded(range) {
                return (
                    !!this.loadedRange &&
                    this.loadedLocationId === this.practiceLocationId &&
                    !range.from.isBefore(this.loadedRange.from, 'day') &&
                    !range.to.isAfter(this.loadedRange.to, 'day')
                )
            },

            getSchedule(userId, practiceId, dateFrom, dateTo, practiceLocationId) {
                this.loading = true
                this.loadError = ''
                UsersAPI.getSchedule(userId, practiceId, dateFrom, dateTo, practiceLocationId)
                    .then((res) => {
                        this.dates = res
                        this.loadedRange = { from: dayjs(dateFrom), to: dayjs(dateTo) }
                        this.loadedLocationId = practiceLocationId
                        this.updateReferralCount(res)
                    })
                    .catch(() => {
                        this.loadError = this.$t('elements.availability.schedule_load_error')
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },

            updateReferralCount(dates) {
                let count = 0
                Object.values(dates).forEach((slots) => {
                    slots.forEach((slot) => {
                        if (
                            slot.referral &&
                            slot.referral.status !== ReferralStatusEnum.NO_SHOW &&
                            slot.referral.status !== ReferralStatusEnum.CANCELLED
                        ) {
                            count += 1
                        }
                    })
                })
                Store.commit('availability/SET_REFERRAL_COUNT', count)
            },

            changeShowCancelled(ev) {
                this.showCancelled = ev.showCancelled
            },
        },

        watch: {
            '$route.query'(newQuery, oldQuery) {
                const dateChanged = newQuery.date !== oldQuery.date
                const viewChanged = newQuery.view !== oldQuery.view
                if (dateChanged || viewChanged) {
                    if (dateChanged) {
                        const date = newQuery.date ? dayjs(newQuery.date) : dayjs()
                        this.year = date.year()
                        this.month = date.month()
                    }
                    this.getCalendarDays()
                }
            },

            practiceLocationId() {
                this.getCalendarDays()
            },
        },

        mounted() {
            if (this.currentDate) {
                this.year = dayjs(this.currentDate).year()
                this.month = dayjs(this.currentDate).month()
            }
            this.getCalendarDays()
            this.handleScheduleChanged = () => this.getCalendarDays(true)
            this.$root.$on('schedule-changed', this.handleScheduleChanged)
        },

        beforeDestroy() {
            this.$root.$off('schedule-changed', this.handleScheduleChanged)
        },
    }
</script>

<style scoped>
    .availability-schedule--with-scroll-container {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;
    }

    .availability-schedule__scroll-container {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        width: 100%;
        display: flex;
        flex-direction: column;
    }

    .availability-schedule__banner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 10px 14px;
        margin: 0 24px 12px;
        border-radius: 8px;
        font-size: 13px;
        border: 1px solid transparent;
    }

    .availability-schedule__banner--error {
        background: #fef2f2;
        color: #b91c1c;
        border-color: #fecaca;
    }

    .availability-schedule__retry {
        background: #ffffff;
        border: 1px solid #fca5a5;
        color: #b91c1c;
        padding: 4px 12px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
    }

    .availability-schedule__retry:hover {
        background: #fee2e2;
    }
</style>
